import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getRiders, assignRiderToOrder } from '../../services/api';

/**
 * MVP de asignación de repartidor desde la comanda (NER-1237).
 * Muestra un dropdown de repartidores disponibles y asigna uno al pedido
 * de delivery vía POST /orders/{orderId}/assign-rider.
 */
function AssignRiderControl({ order, onAssigned }) {
  const { t } = useTranslation();
  const [riders, setRiders] = useState([]);
  const [selectedRiderId, setSelectedRiderId] = useState('');
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState('');
  const [assignedRider, setAssignedRider] = useState(null);

  const loadRiders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getRiders();
      const available = (data?.riders || []).filter((r) => r.status === 'available');
      setRiders(available);
    } catch (err) {
      console.error('Error loading riders:', err);
      setError(t('orders.delivery.assignError', 'No se pudieron cargar los repartidores'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadRiders();
  }, [loadRiders]);

  const handleAssign = async () => {
    if (!selectedRiderId) return;
    try {
      setAssigning(true);
      setError('');
      const result = await assignRiderToOrder(order.orderId, selectedRiderId);
      const rider = result?.deliveryRider || riders.find((r) => r.riderId === selectedRiderId);
      setAssignedRider(rider);
      if (onAssigned) onAssigned(result);
    } catch (err) {
      console.error('Error assigning rider:', err);
      setError(t('orders.delivery.assignFailed', 'Error al asignar el repartidor'));
    } finally {
      setAssigning(false);
    }
  };

  if (assignedRider) {
    return (
      <div className="order-modal-info-row">
        <span className="material-icons" aria-hidden="true">
          check_circle
        </span>
        <div className="order-modal-info-text">
          <span className="order-modal-info-label">
            {t('orders.delivery.rider', 'Repartidor')}
          </span>
          <span className="order-modal-info-value">{assignedRider.name}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="order-modal-assign-rider">
      <span className="order-modal-info-label">
        {t('orders.delivery.assignRiderLabel', 'Asignar repartidor')}
      </span>
      {loading ? (
        <span className="order-modal-info-secondary">{t('common.loading', 'Cargando...')}</span>
      ) : riders.length === 0 ? (
        <span className="order-modal-info-secondary">
          {t('orders.delivery.noRidersAvailable', 'No hay repartidores disponibles')}
        </span>
      ) : (
        <div className="order-modal-assign-rider-controls">
          <select
            className="order-modal-assign-rider-select"
            value={selectedRiderId}
            onChange={(e) => setSelectedRiderId(e.target.value)}
            aria-label={t('orders.delivery.assignRiderLabel', 'Asignar repartidor')}
          >
            <option value="">
              {t('orders.delivery.assignRiderPlaceholder', 'Seleccionar...')}
            </option>
            {riders.map((r) => (
              <option key={r.riderId} value={r.riderId}>
                {r.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!selectedRiderId || assigning}
            onClick={handleAssign}
          >
            {assigning
              ? t('orders.delivery.assigning', 'Asignando...')
              : t('orders.delivery.assignButton', 'Asignar')}
          </button>
        </div>
      )}
      {error && <span className="order-modal-assign-rider-error">{error}</span>}
    </div>
  );
}

export default AssignRiderControl;
